// Hermes собирается без Intl.PluralRules, а i18next 25 выбирает форму
// множественного числа только через него: без полифилла КАЖДЫЙ плюральный ключ
// («10 дней подряд», «Осталось 11 дней», «Прогресс 10 из 21 дня») возвращался
// на устройстве сырым — строкой вида «flame.streakDays». Импорт обязан стоять
// до init: правила читаются при инициализации.
import 'intl-pluralrules';
import i18n from 'i18next';
import { initReactI18next, useTranslation as useI18nextTranslation } from 'react-i18next';
import { getLocales } from 'expo-localization';
import { storage } from '../utils/storage';
import { pickInitialLanguage } from './pickInitialLanguage';

// English
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enHome from './locales/en/home.json';
import enMasters from './locales/en/masters.json';
import enPractices from './locales/en/practices.json';
import enLives from './locales/en/lives.json';
import enDashboard from './locales/en/dashboard.json';
import enAbout from './locales/en/about.json';
import enSettings from './locales/en/settings.json';
import enChats from './locales/en/chats.json';
import enLanding from './locales/en/landing.json';
import enFund from './locales/en/fund.json';
import enEvents from './locales/en/events.json';
import enGamification from './locales/en/gamification.json';

// Russian
import ruCommon from './locales/ru/common.json';
import ruAuth from './locales/ru/auth.json';
import ruHome from './locales/ru/home.json';
import ruMasters from './locales/ru/masters.json';
import ruPractices from './locales/ru/practices.json';
import ruLives from './locales/ru/lives.json';
import ruDashboard from './locales/ru/dashboard.json';
import ruAbout from './locales/ru/about.json';
import ruSettings from './locales/ru/settings.json';
import ruChats from './locales/ru/chats.json';
import ruLanding from './locales/ru/landing.json';
import ruFund from './locales/ru/fund.json';
import ruEvents from './locales/ru/events.json';
import ruGamification from './locales/ru/gamification.json';

// Spanish
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esHome from './locales/es/home.json';
import esMasters from './locales/es/masters.json';
import esPractices from './locales/es/practices.json';
import esLives from './locales/es/lives.json';
import esDashboard from './locales/es/dashboard.json';
import esAbout from './locales/es/about.json';
import esSettings from './locales/es/settings.json';
import esChats from './locales/es/chats.json';
import esLanding from './locales/es/landing.json';
import esFund from './locales/es/fund.json';
import esEvents from './locales/es/events.json';
import esGamification from './locales/es/gamification.json';

const NAMESPACES = ['common', 'auth', 'home', 'masters', 'practices', 'lives', 'dashboard', 'about', 'settings', 'chats', 'landing', 'fund', 'events', 'gamification'] as const;

// CORE-573: region-aware initial language. See pickInitialLanguage for the
// policy and rationale (RU-first; RU-region English phones → Russian).
function getDeviceLanguage(): string {
  try {
    return pickInitialLanguage(getLocales());
  } catch {
    return 'ru';
  }
}

const setDocumentLang = (lang: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
};

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      auth: enAuth,
      home: enHome,
      masters: enMasters,
      practices: enPractices,
      lives: enLives,
      dashboard: enDashboard,
      about: enAbout,
      settings: enSettings,
      chats: enChats,
      landing: enLanding,
      fund: enFund,
      events: enEvents,
      gamification: enGamification,
    },
    ru: {
      common: ruCommon,
      auth: ruAuth,
      home: ruHome,
      masters: ruMasters,
      practices: ruPractices,
      lives: ruLives,
      dashboard: ruDashboard,
      about: ruAbout,
      settings: ruSettings,
      chats: ruChats,
      landing: ruLanding,
      fund: ruFund,
      events: ruEvents,
      gamification: ruGamification,
    },
    es: {
      common: esCommon,
      auth: esAuth,
      home: esHome,
      masters: esMasters,
      practices: esPractices,
      lives: esLives,
      dashboard: esDashboard,
      about: esAbout,
      settings: esSettings,
      chats: esChats,
      landing: esLanding,
      fund: esFund,
      events: esEvents,
      gamification: esGamification,
    },
  },
  lng: getDeviceLanguage(),
  // Russian is the source/most-complete language and the product default;
  // fall back to it (not English) for any key gap so we never leak an English
  // string into an otherwise-Russian UI. CORE-573.
  fallbackLng: 'ru',
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
});

// CORE-1715 — пока переключатель языка скрыт флагом, интерфейс держится
// на русском: иначе человек с сохранённым en/es или испанским браузером не может
// вернуться на русский — кнопки нет. Выбор при этом не стирается: ни хранилище,
// ни сервер не трогаем, и с включением флага язык человека возвращается.
let lockedToRussian = false;

// Load stored language preference (async for native AsyncStorage)
storage.getLanguage().then((stored) => {
  if (lockedToRussian) return;
  const lang = stored && ['en', 'ru', 'es'].includes(stored) ? stored : i18n.language;
  if (lang !== i18n.language) i18n.changeLanguage(lang);
  setDocumentLang(lang);
});

/**
 * CORE-1715 — зовётся из корня приложения (веб и нативка), когда `/api/features`
 * ответил: `true` — переключателя нет, показываем русский; `false` —
 * переключатель есть, возвращаем сохранённый язык (или язык устройства).
 */
export function setLanguageLockedToRussian(locked: boolean): Promise<unknown> {
  if (locked === lockedToRussian) return Promise.resolve();
  lockedToRussian = locked;
  if (locked) {
    setDocumentLang('ru');
    return i18n.language === 'ru' ? Promise.resolve() : i18n.changeLanguage('ru');
  }
  return storage.getLanguage().then((stored) => {
    if (lockedToRussian) return;
    const lang = stored && ['en', 'ru', 'es'].includes(stored) ? stored : getDeviceLanguage();
    setDocumentLang(lang);
    if (lang !== i18n.language) return i18n.changeLanguage(lang);
  });
}

/**
 * useI18n — thin wrapper around react-i18next's useTranslation.
 * Named differently to avoid conflict with the audio useTranslation hook.
 */
export function useI18n(ns?: string | string[]) {
  return useI18nextTranslation(ns);
}

export function changeLanguage(lang: string) {
  storage.setLanguage(lang);
  setDocumentLang(lang);
  // Best-effort sync to BE so transactional emails (confirmation, password
  // reset, live reminders) follow the user's UI choice. Skipped when the
  // user isn't logged in or when the network call fails — i18n switching
  // must never block on remote state. Lazy-import keeps storage/api out of
  // the module-init cycle (api → storage → i18n would deadlock at startup).
  void (async () => {
    try {
      const token = await storage.getToken();
      if (!token) return; // anon users: nothing to persist server-side
      const { api } = await import('../services/api');
      await api.updateLanguage(lang);
    } catch {
      // Swallow — UI language already changed locally; the BE will catch
      // up next time the user toggles or on the next successful call.
    }
  })();
  return i18n.changeLanguage(lang);
}

export function getCurrentLanguage(): string {
  return i18n.language;
}

/**
 * useCurrentLanguage — the single, reactive way to read the active language.
 * Re-renders the consumer on language change (via react-i18next). Replaces the
 * 5+ ad-hoc idioms scattered across screens (`const language = i18n.language`,
 * `const ru = i18n.language === 'ru'`, `locale.startsWith('ru')`, …). Migrate
 * call sites to this incrementally.
 */
export function useCurrentLanguage(): string {
  const { i18n: instance } = useI18nextTranslation();
  return instance.language;
}

export default i18n;
